import { z } from 'zod';

// Single source of truth for non-secret per-environment config.
// Schema lives at config/environments.schema.json. CI validates it via
// scripts/validate-env.ts on every PR touching config/.
import environmentsConfig from '../../../config/environments.json' with { type: 'json' };

// ============================================================================
// Environment Detection
// ============================================================================

const envSchema = z.object({
  MODE: z.enum(['development', 'staging', 'production']).default('development'),
});

type Environment = z.infer<typeof envSchema>['MODE'];

function detectEnvironment(): Environment {
  // Check window (browser runtime override)
  if (typeof window !== 'undefined') {
    const windowEnv = (window as Window & { __ENV__?: { MODE?: string } }).__ENV__?.MODE;
    if (windowEnv) {
      const parsed = envSchema.shape.MODE.safeParse(windowEnv);
      if (parsed.success) return parsed.data;
    }
  }

  // Next must select the deployment target independently of NODE_ENV:
  // optimized development and staging images also use NODE_ENV=production.
  try {
    const deployment = envSchema.shape.MODE.safeParse(process.env.NEXT_PUBLIC_DEPLOY_ENV);
    if (process.env.NEXT_PUBLIC_DEPLOY_ENV && deployment.success) return deployment.data;
  } catch {
    // Vite browsers have no process global; continue with import.meta.env.
  }

  // Check Vite's import.meta.env (browser bundle, statically replaced at build)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const viteMeta = typeof import.meta !== 'undefined' ? (import.meta as any).env : undefined;
  if (viteMeta) {
    const viteEnvVar = viteMeta.VITE_ENV || viteMeta.VITE_MODE || viteMeta.MODE;
    if (viteEnvVar) {
      const parsed = envSchema.shape.MODE.safeParse(viteEnvVar);
      if (parsed.success) return parsed.data;
    }
    if (viteMeta.PROD === true) return 'production';
  }

  // Check process.env (Node.js / Next.js)
  if (typeof process !== 'undefined' && process.env) {
    const nodeMode = process.env.VITE_ENV || process.env.MODE || process.env.VITE_MODE;
    if (nodeMode) {
      const parsed = envSchema.shape.MODE.safeParse(nodeMode);
      if (parsed.success) return parsed.data;
    }

    const nodeEnv = process.env.NODE_ENV;
    if (nodeEnv === 'production') return 'production';
    if (nodeEnv === 'test') return 'development';
  }

  return 'development';
}

export const ENV = detectEnvironment();
export const isDev = ENV === 'development';
export const isStaging = ENV === 'staging';
export const isProd = ENV === 'production';

// ============================================================================
// Environment Configuration
// ============================================================================

export type ProductStatus = 'enabled' | 'disabled' | 'fake_door';

export interface EnvironmentConfig {
  // URLs
  apiUrl: string;
  appUrl: string;
  adminUrl: string;
  wsUrl: string;
  onboardingApiUrl: string;

  // JAAK Identity Verification
  jaak: {
    apiUrl: string;
    apiKey: string;
    /** License key para Stamps (Document Capture SDK) */
    stampsLicenseKey: string;
    /** License key para Visage (Face Capture SDK) */
    visageLicenseKey: string;
    /** Ambiente del SDK: dev, qa, sandbox, prod */
    sdkEnvironment: 'dev' | 'qa' | 'sandbox' | 'prod';
  };

  // Tenant
  tenantId: string;

  // Timeouts (in milliseconds)
  sessionTimeout: number;
  inactivityTimeout: number;
  sessionWarningBefore: number;

  // Feature Flags
  featureFlagPollInterval: number;

  // Observability
  sentryDsn: string | null;
  sentryEnvironment: string;

  // Features
  enableDevTools: boolean;
  enableMockApi: boolean;
  enablePushNotifications: boolean;
  /**
   * Habilita el flujo de onboarding Persona Moral (PM) + el selector PF/PM.
   * Cuando es `false` (default en staging/producción), el selector no se
   * muestra y el registro entra directo a Persona Física. Override por build
   * con VITE_ONBOARDING_MORAL_ENABLED / NEXT_PUBLIC_ONBOARDING_MORAL_ENABLED.
   */
  onboardingMoralEnabled: boolean;
  /**
   * Enciende `use-document-classification` en el SDK de captura de INE (JAAK
   * Stamps) para que rechace el segundo anverso antes de subirlo. Default
   * `false` hasta validarlo desplegado. Override con
   * VITE_JAAK_DOCUMENT_CLASSIFICATION_ENABLED / NEXT_PUBLIC_JAAK_DOCUMENT_CLASSIFICATION_ENABLED.
   */
  jaakDocumentClassificationEnabled: boolean;
  /**
   * Habilita el ítem "Productos" (catálogo /products, sección Explorar del
   * sidebar). Default `false` (oculto) en los 3 entornos. Override con
   * VITE_PRODUCTS_CATALOG_ENABLED / NEXT_PUBLIC_PRODUCTS_CATALOG_ENABLED.
   */
  productsCatalogEnabled: boolean;
  logLevel: 'debug' | 'info' | 'warn' | 'error';

  // Cloudflare Turnstile (bot protection on registration)
  turnstile: {
    /** Whether the widget is active. Always false until BE ships siteverify. */
    enabled: boolean;
    /** Public site key — safe to expose in client bundle. */
    siteKey: string;
  };

  // Google Tag Manager
  gtm: {
    /** Whether GTM is active. Set VITE_GTM_ENABLED=true in staging/production. */
    enabled: boolean;
    /** Container ID from tagmanager.google.com — format GTM-XXXXXXX. Safe to expose. */
    containerId: string;
  };

  // OAuth 2.0 + PKCE
  oauth: {
    clientId: string;
    /**
     * Confidential-client secret. Empty string in dev (public client).
     * Populated in staging/prod via VITE_OAUTH_CLIENT_SECRET while the FE
     * runs the password grant; reverts to '' once we migrate to
     * authorization_code + PKCE + DPoP and the BE switches the client back
     * to public.
     */
    clientSecret: string;
    authorizeUrl: string;
    tokenUrl: string;
    revokeUrl: string;
    redirectUri: string;
    defaultScopes: string;
  };

  // Product availability
  products: {
    investment: ProductStatus;
    credit: ProductStatus;
    savings_box: ProductStatus;
    rotating_savings: ProductStatus;
    tanda: ProductStatus;
    savings_box_withdraw: ProductStatus;
  };

  // E-sign delivery channels enabled for this tenant/environment
  esignChannels: ('email' | 'sms' | 'whatsapp')[];

  // Seconds to count down before auto-redirecting after e-sign completion
  esignRedirectCountdown: number;

  // Default banking institution name shown when the API doesn't provide one.
  // Override with VITE_DEFAULT_BANK_NAME / NEXT_PUBLIC_DEFAULT_BANK_NAME.
  defaultBankName: string;

  // Contact emails — override via VITE_SUPPORT_EMAIL / VITE_CREDIT_EMAIL / VITE_CONTACT_EMAIL
  contacts: {
    /** Customer support — soporte@tankef.com */
    support: string;
    /** Credit inquiries — credito@tankef.com */
    credit: string;
    /** General product contact — contacto@tankef.com */
    contact: string;
  };
}

// JSON shape uses *Ms suffix for time values for self-documentation in the
// non-typed consumers (jq, CI). Map to the existing camelCase TS interface
// once at import time.
type RawEnvConfig =
  (typeof environmentsConfig)['environments'][keyof (typeof environmentsConfig)['environments']];

function fromJson(raw: RawEnvConfig): EnvironmentConfig {
  return {
    apiUrl: raw.apiUrl,
    appUrl: raw.appUrl,
    adminUrl: raw.adminUrl,
    wsUrl: raw.wsUrl,
    onboardingApiUrl: raw.onboardingApiUrl,
    tenantId: raw.tenantId,
    sessionTimeout: raw.sessionTimeoutMs,
    inactivityTimeout: raw.inactivityTimeoutMs,
    sessionWarningBefore: raw.sessionWarningBeforeMs,
    featureFlagPollInterval: raw.featureFlagPollIntervalMs,
    sentryDsn: raw.sentryDsn,
    sentryEnvironment: raw.sentryEnvironment,
    enableDevTools: raw.enableDevTools,
    enableMockApi: raw.enableMockApi,
    enablePushNotifications: raw.enablePushNotifications,
    onboardingMoralEnabled: raw.onboardingMoralEnabled,
    // Sin valor por entorno: apagado salvo bandera de build (ver abajo).
    jaakDocumentClassificationEnabled: false,
    productsCatalogEnabled: raw.productsCatalogEnabled,
    logLevel: raw.logLevel as 'debug' | 'info' | 'warn' | 'error',
    oauth: { ...raw.oauth },
    jaak: {
      apiUrl: raw.jaak.apiUrl,
      apiKey: raw.jaak.apiKey,
      stampsLicenseKey: raw.jaak.stampsLicenseKey,
      visageLicenseKey: raw.jaak.visageLicenseKey,
      sdkEnvironment: raw.jaak.sdkEnvironment as 'dev' | 'qa' | 'sandbox' | 'prod',
    },
    turnstile: { ...raw.turnstile },
    gtm: { ...raw.gtm },
    products: {
      investment: raw.products.investment as ProductStatus,
      credit: raw.products.credit as ProductStatus,
      savings_box: raw.products.savings_box as ProductStatus,
      rotating_savings: raw.products.rotating_savings as ProductStatus,
      tanda: raw.products.tanda as ProductStatus,
      savings_box_withdraw: raw.products.savings_box_withdraw as ProductStatus,
    },
    esignChannels: raw.esignChannels as ('email' | 'sms' | 'whatsapp')[],
    esignRedirectCountdown: raw.esignRedirectCountdown,
    defaultBankName: raw.defaultBankName,
    contacts: { ...raw.contacts },
  };
}

const configs: Record<Environment, EnvironmentConfig> = {
  development: fromJson(environmentsConfig.environments.development),
  staging: fromJson(environmentsConfig.environments.staging),
  production: fromJson(environmentsConfig.environments.production),
};

// ============================================================================
// Runtime overrides from Vite build args (values sourced from GCP Secret Manager)
// These take precedence over the static per-environment values above.
// ============================================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _viteEnv = typeof import.meta !== 'undefined' ? (import.meta as any).env : undefined;
const _processEnv = typeof process !== 'undefined' ? process.env : undefined;

/**
 * Next reemplaza `process.env.NEXT_PUBLIC_*` en el bundle del cliente **sólo
 * cuando el acceso es estático**. `readPublicEnv` las busca por índice
 * (`_processEnv?.[key]`), que Next no puede resolver en build: el resultado es
 * `undefined` y la configuración cae al default del entorno.
 *
 * Efecto práctico: el backoffice ignoraba `NEXT_PUBLIC_API_URL` y seguía
 * pegándole al backend de desarrollo remoto aunque el `.env.local` dijera
 * `localhost`. Nadie podía probar el panel contra su propio backend.
 *
 * Este mapa las nombra una por una para que el reemplazo ocurra. Sólo aplica a
 * Next; en Vite `import.meta.env` ya funciona por índice.
 *
 * El `try/catch` es obligatorio, no defensivo de más: este paquete también lo
 * carga la app **Vite** (`apps/web`), donde `process` no existe en el navegador
 * y un `process.env` sin proteger lanza `ReferenceError: process is not
 * defined` al evaluar el módulo — es decir, la app entera deja de arrancar.
 * No sirve leerlas vía `_processEnv?.[key]`: Next sólo sustituye el acceso
 * ESTÁTICO `process.env.NEXT_PUBLIC_X`, que es justo lo que hay que conservar
 * aquí dentro. Tras la sustitución de Next el cuerpo son literales, así que el
 * `catch` sólo se dispara en el navegador de Vite.
 */
const _nextPublicEnv: Record<string, string | undefined> = (() => {
  try {
    return {
      NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
      NEXT_PUBLIC_ONBOARDING_API_URL: process.env.NEXT_PUBLIC_ONBOARDING_API_URL,
      NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
      NEXT_PUBLIC_ADMIN_URL: process.env.NEXT_PUBLIC_ADMIN_URL,
      NEXT_PUBLIC_WS_URL: process.env.NEXT_PUBLIC_WS_URL,
      NEXT_PUBLIC_TENANT_ID: process.env.NEXT_PUBLIC_TENANT_ID,
      NEXT_PUBLIC_OAUTH_CLIENT_ID: process.env.NEXT_PUBLIC_OAUTH_CLIENT_ID,
      NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
      // Las URLs de OAuth son independientes de `apiUrl`: apuntar el panel a
      // un backend local no bastaba, porque el login seguía resolviendo su
      // tokenUrl desde el perfil del entorno y viajaba al backend remoto.
      // Cualquier clave ausente de este mapa es invisible en el cliente, no
      // un default vacío.
      NEXT_PUBLIC_OAUTH_CLIENT_SECRET: process.env.NEXT_PUBLIC_OAUTH_CLIENT_SECRET,
      NEXT_PUBLIC_OAUTH_TOKEN_URL: process.env.NEXT_PUBLIC_OAUTH_TOKEN_URL,
      NEXT_PUBLIC_OAUTH_AUTHORIZE_URL: process.env.NEXT_PUBLIC_OAUTH_AUTHORIZE_URL,
      NEXT_PUBLIC_OAUTH_REVOKE_URL: process.env.NEXT_PUBLIC_OAUTH_REVOKE_URL,
      NEXT_PUBLIC_OAUTH_REDIRECT_URI: process.env.NEXT_PUBLIC_OAUTH_REDIRECT_URI,
    };
  } catch {
    // Navegador sin `process` (Vite): las variables se leen por
    // `import.meta.env` en `readPublicEnv`, así que este mapa sobra.
    return {};
  }
})();

const _baseConfig = configs[ENV];

function readPublicEnv(...keys: string[]): string | undefined {
  for (const key of keys) {
    const viteValue = _viteEnv?.[key];
    if (typeof viteValue === 'string' && viteValue.length > 0) {
      return viteValue;
    }

    // Estático primero: en el cliente de Next es la ÚNICA forma de leerlas
    // (ver `_nextPublicEnv`). En servidor el acceso por índice de abajo
    // también funciona, pero este ya devolvió el valor correcto.
    const nextValue = _nextPublicEnv[key];
    if (typeof nextValue === 'string' && nextValue.length > 0) {
      return nextValue;
    }

    const processValue = _processEnv?.[key];
    if (typeof processValue === 'string' && processValue.length > 0) {
      return processValue;
    }
  }

  return undefined;
}

function readProductStatus(key: string, fallback: ProductStatus): ProductStatus {
  const val = readPublicEnv(key);
  if (val === 'enabled' || val === 'disabled' || val === 'fake_door') return val;
  return fallback;
}

// Read a boolean flag from env vars, falling back to the JSON-derived value.
// Without this fallback, a missing env var silently flips the flag to false
// and overrides the JSON's intent (e.g. prod gtm.enabled was getting ignored
// in any context that didn't explicitly set VITE_GTM_ENABLED=true).
function readBoolean(keys: string[], fallback: boolean): boolean {
  const val = readPublicEnv(...keys);
  if (val === 'true') return true;
  if (val === 'false') return false;
  return fallback;
}

function readEsignChannels(
  fallback: ('email' | 'sms' | 'whatsapp')[]
): ('email' | 'sms' | 'whatsapp')[] {
  const raw = readPublicEnv('VITE_ESIGN_CHANNELS');
  if (!raw) return fallback;
  const valid = ['email', 'sms', 'whatsapp'] as const;
  const parsed = raw
    .split(',')
    .map(s => s.trim().toLowerCase())
    .filter((s): s is 'email' | 'sms' | 'whatsapp' => (valid as readonly string[]).includes(s));
  return parsed.length > 0 ? parsed : fallback;
}

export const config: EnvironmentConfig = {
  ..._baseConfig,
  enableMockApi: readPublicEnv('VITE_ENABLE_MOCK_API') === 'true' || _baseConfig.enableMockApi,
  enablePushNotifications:
    readPublicEnv('VITE_FEATURE_PUSH_NOTIFICATIONS') === 'true' ||
    _baseConfig.enablePushNotifications,
  // Fallback = valor por entorno (staging/prod: false). Si el flag de build no
  // viene, NO queda enabled por accidente (safe-by-default: PM deshabilitado).
  onboardingMoralEnabled: readBoolean(
    ['VITE_ONBOARDING_MORAL_ENABLED', 'NEXT_PUBLIC_ONBOARDING_MORAL_ENABLED'],
    _baseConfig.onboardingMoralEnabled
  ),
  jaakDocumentClassificationEnabled: readBoolean(
    [
      'VITE_JAAK_DOCUMENT_CLASSIFICATION_ENABLED',
      'NEXT_PUBLIC_JAAK_DOCUMENT_CLASSIFICATION_ENABLED',
    ],
    _baseConfig.jaakDocumentClassificationEnabled
  ),
  productsCatalogEnabled: readBoolean(
    ['VITE_PRODUCTS_CATALOG_ENABLED', 'NEXT_PUBLIC_PRODUCTS_CATALOG_ENABLED'],
    _baseConfig.productsCatalogEnabled
  ),
  apiUrl: readPublicEnv('VITE_API_URL', 'NEXT_PUBLIC_API_URL') || _baseConfig.apiUrl,
  appUrl: readPublicEnv('VITE_APP_URL', 'NEXT_PUBLIC_APP_URL') || _baseConfig.appUrl,
  adminUrl: readPublicEnv('VITE_ADMIN_URL', 'NEXT_PUBLIC_ADMIN_URL') || _baseConfig.adminUrl,
  // Allow tenant ID to be injected at build time from environment
  tenantId: readPublicEnv('VITE_TENANT_ID', 'NEXT_PUBLIC_TENANT_ID') || _baseConfig.tenantId,
  onboardingApiUrl:
    readPublicEnv('VITE_ONBOARDING_API_URL', 'NEXT_PUBLIC_ONBOARDING_API_URL') ||
    _baseConfig.onboardingApiUrl,
  sentryDsn: readPublicEnv('VITE_SENTRY_DSN', 'NEXT_PUBLIC_SENTRY_DSN') || _baseConfig.sentryDsn,
  // Allow WebSocket URL to be overridden via public env vars
  wsUrl: readPublicEnv('VITE_WS_URL', 'NEXT_PUBLIC_WS_URL') || _baseConfig.wsUrl,
  oauth: {
    ..._baseConfig.oauth,
    clientId:
      readPublicEnv('VITE_OAUTH_CLIENT_ID', 'NEXT_PUBLIC_OAUTH_CLIENT_ID') ||
      _baseConfig.oauth.clientId,
    clientSecret:
      readPublicEnv('VITE_OAUTH_CLIENT_SECRET', 'NEXT_PUBLIC_OAUTH_CLIENT_SECRET') ||
      _baseConfig.oauth.clientSecret,
    tokenUrl:
      readPublicEnv('VITE_OAUTH_TOKEN_URL', 'NEXT_PUBLIC_OAUTH_TOKEN_URL') ||
      _baseConfig.oauth.tokenUrl,
    authorizeUrl:
      readPublicEnv('VITE_OAUTH_AUTHORIZE_URL', 'NEXT_PUBLIC_OAUTH_AUTHORIZE_URL') ||
      _baseConfig.oauth.authorizeUrl,
    revokeUrl:
      readPublicEnv('VITE_OAUTH_REVOKE_URL', 'NEXT_PUBLIC_OAUTH_REVOKE_URL') ||
      _baseConfig.oauth.revokeUrl,
    redirectUri:
      readPublicEnv('VITE_OAUTH_REDIRECT_URI', 'NEXT_PUBLIC_OAUTH_REDIRECT_URI') ||
      _baseConfig.oauth.redirectUri,
  },
  turnstile: {
    enabled: readBoolean(
      ['VITE_TURNSTILE_ENABLED', 'NEXT_PUBLIC_TURNSTILE_ENABLED'],
      _baseConfig.turnstile.enabled
    ),
    siteKey:
      readPublicEnv('VITE_TURNSTILE_SITE_KEY', 'NEXT_PUBLIC_TURNSTILE_SITE_KEY') ||
      _baseConfig.turnstile.siteKey,
  },

  gtm: {
    enabled: readBoolean(['VITE_GTM_ENABLED', 'NEXT_PUBLIC_GTM_ENABLED'], _baseConfig.gtm.enabled),
    containerId:
      readPublicEnv('VITE_GTM_CONTAINER_ID', 'NEXT_PUBLIC_GTM_CONTAINER_ID') ||
      _baseConfig.gtm.containerId,
  },
  products: {
    investment: readProductStatus('VITE_PRODUCT_INVESTMENT', _baseConfig.products.investment),
    credit: readProductStatus('VITE_PRODUCT_CREDIT', _baseConfig.products.credit),
    savings_box: readProductStatus('VITE_PRODUCT_SAVINGS_BOX', _baseConfig.products.savings_box),
    rotating_savings: readProductStatus(
      'VITE_PRODUCT_ROTATING_SAVINGS',
      _baseConfig.products.rotating_savings
    ),
    tanda: readProductStatus('VITE_PRODUCT_TANDA', _baseConfig.products.tanda),
    savings_box_withdraw: readProductStatus(
      'VITE_PRODUCT_SAVINGS_BOX_WITHDRAW',
      _baseConfig.products.savings_box_withdraw
    ),
  },
  esignChannels: readEsignChannels(_baseConfig.esignChannels),
  esignRedirectCountdown: (() => {
    const raw = readPublicEnv('VITE_ESIGN_REDIRECT_COUNTDOWN');
    if (!raw) return _baseConfig.esignRedirectCountdown;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : _baseConfig.esignRedirectCountdown;
  })(),
  defaultBankName:
    readPublicEnv('VITE_DEFAULT_BANK_NAME', 'NEXT_PUBLIC_DEFAULT_BANK_NAME') ||
    _baseConfig.defaultBankName,
  contacts: {
    support:
      readPublicEnv('VITE_SUPPORT_EMAIL', 'NEXT_PUBLIC_SUPPORT_EMAIL') ||
      _baseConfig.contacts.support,
    credit:
      readPublicEnv('VITE_CREDIT_EMAIL', 'NEXT_PUBLIC_CREDIT_EMAIL') || _baseConfig.contacts.credit,
    contact:
      readPublicEnv('VITE_CONTACT_EMAIL', 'NEXT_PUBLIC_CONTACT_EMAIL') ||
      _baseConfig.contacts.contact,
  },
};

export type JaakSdkEnvironment = 'dev' | 'qa' | 'sandbox' | 'prod';

// Single source of truth: each JAAK SDK environment maps to exactly one
// validation API. Deriving `apiUrl` from `sdkEnvironment` eliminates the
// drift class that caused tickets where dev pointed at sandbox keys but
// the SDK was validating against services.api.jaak.ai (prod).
const JAAK_API_URLS: Record<JaakSdkEnvironment, string> = {
  dev: 'https://services.api.jaak.ai',
  qa: 'https://services.api.jaak.ai',
  sandbox: 'https://services.api.jaak.ai',
  prod: 'https://services.api.jaak.ai',
};

function isJaakSdkEnvironment(value: unknown): value is JaakSdkEnvironment {
  return value === 'dev' || value === 'qa' || value === 'sandbox' || value === 'prod';
}

/**
 * Get JAAK configuration - call this at runtime in browser context.
 *
 * Resolution priority for `sdkEnvironment` (most authoritative wins):
 *   1. `VITE_JAAK_SDK_ENVIRONMENT` (intentional local override — devs / QA)
 *   2. `baseConfig.sdkEnvironment` (per-env default from environments.json)
 *
 * The licenses themselves come from the backend (`/kyc/sdk-config`), so
 * callers should prefer those over `stampsLicenseKey` / `visageLicenseKey`
 * from this helper — those are only fallbacks for dev when no session
 * exists yet.
 *
 * `apiUrl` is DERIVED from `sdkEnvironment` so the two cannot drift.
 * Even if `environments.json` accidentally pointed `apiUrl` at prod for a
 * non-prod env, the derived value here will still match `sdkEnvironment`.
 */
export function getJaakConfig(): {
  apiUrl: string;
  apiKey: string;
  stampsLicenseKey: string;
  visageLicenseKey: string;
  sdkEnvironment: JaakSdkEnvironment;
} {
  const baseConfig = config.jaak;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const viteEnv = typeof import.meta !== 'undefined' ? (import.meta as any).env : undefined;
  // NOTE: `apiKey` is intentionally NOT read from any VITE_* env variable.
  // Vite inlines VITE_* values into the public bundle, which would leak the
  // JAAK server credential. KYC must go through the Tankef backend proxy.
  // See the security note in `packages/api-client/src/index.ts > createJaakClient`.
  const apiKey = baseConfig.apiKey;
  const stampsLicenseKey = viteEnv?.VITE_JAAK_STAMPS_LICENSE || baseConfig.stampsLicenseKey;
  const visageLicenseKey = viteEnv?.VITE_JAAK_VISAGE_LICENSE || baseConfig.visageLicenseKey;

  const sdkEnvironmentOverride = viteEnv?.VITE_JAAK_SDK_ENVIRONMENT;
  const sdkEnvironment: JaakSdkEnvironment = isJaakSdkEnvironment(sdkEnvironmentOverride)
    ? sdkEnvironmentOverride
    : isJaakSdkEnvironment(baseConfig.sdkEnvironment)
      ? baseConfig.sdkEnvironment
      : 'sandbox';

  return {
    apiUrl: JAAK_API_URLS[sdkEnvironment],
    apiKey,
    stampsLicenseKey,
    visageLicenseKey,
    sdkEnvironment,
  };
}

// ============================================================================
// URL Helpers
// ============================================================================

export function getApiUrl(path: string): string {
  const base = config.apiUrl.endsWith('/') ? config.apiUrl.slice(0, -1) : config.apiUrl;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${cleanPath}`;
}

export function getWsUrl(path: string = '/cable'): string {
  const base = config.wsUrl.endsWith('/') ? config.wsUrl.slice(0, -1) : config.wsUrl;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${cleanPath}`;
}

// ============================================================================
// Logging Helper
// ============================================================================

const LOG_LEVELS = { debug: 0, info: 1, warn: 2, error: 3 };

export function log(level: keyof typeof LOG_LEVELS, message: string, ...args: unknown[]): void {
  if (LOG_LEVELS[level] >= LOG_LEVELS[config.logLevel]) {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    switch (level) {
      case 'debug':
        console.debug(prefix, message, ...args);
        break;
      case 'info':
        console.info(prefix, message, ...args);
        break;
      case 'warn':
        console.warn(prefix, message, ...args);
        break;
      case 'error':
        console.error(prefix, message, ...args);
        break;
    }
  }
}

export const logger = {
  debug: (message: string, ...args: unknown[]) => log('debug', message, ...args),
  info: (message: string, ...args: unknown[]) => log('info', message, ...args),
  warn: (message: string, ...args: unknown[]) => log('warn', message, ...args),
  error: (message: string, ...args: unknown[]) => log('error', message, ...args),
};

/**
 * Lista de keys que NUNCA deben aparecer en valor en logs/analytics/telemetry.
 * LFPDPPP / Ley Fintech Art. 58 / FATF Rec. 10 obligan a minimización + cifrado
 * en tránsito y reposo. Logs no son ni una cosa ni la otra, así que
 * enmascaramos en origen para no depender de que el sink lo haga.
 */
const PII_KEYS = new Set([
  // Identificadores oficiales
  'curp',
  'rfc',
  'tax_id',
  'taxId',
  'document_number',
  'documentNumber',
  'passport_number',
  'passportNumber',
  'cic',
  'ocr',
  // Identidad personal
  'first_name',
  'firstName',
  'middle_name',
  'middleName',
  'second_name',
  'secondName',
  'last_name',
  'lastName',
  'maternal_name',
  'maternalName',
  'maternal_surname',
  'maternalSurname',
  'paternal_surname',
  'paternalSurname',
  'full_name',
  'fullName',
  'name',
  // Contacto
  'email',
  'phone',
  'phone_number',
  'phoneNumber',
  // Sensibles
  'birth_date',
  'birthDate',
  'date_of_birth',
  'dateOfBirth',
  'gender',
  'sex',
  'face',
  // Dirección
  'address',
  'street',
  'neighborhood',
  'postal_code',
  'postalCode',
  'exterior_number',
  'exteriorNumber',
  // Auth
  'access_token',
  'accessToken',
  'refresh_token',
  'refreshToken',
  'password',
]);

const MAX_SANITIZE_DEPTH = 5;

/**
 * Devuelve una versión segura para loggear: las claves marcadas como PII
 * se reemplazan por `'[REDACTED]'` (o `[REDACTED:len=N]` si es string, útil
 * para diferenciar "vino vacío" de "vino con valor").
 *
 * Uso típico:
 *   logger.info('[Mapper] Output:', sanitizePii({ result }));
 *
 * Para shape-only logging (preferido), pasar `{ shapeOnly: true }`.
 */
export function sanitizePii(
  value: unknown,
  opts: { shapeOnly?: boolean; depth?: number } = {}
): unknown {
  const { shapeOnly = false, depth = 0 } = opts;
  if (depth > MAX_SANITIZE_DEPTH) return '[truncated]';
  if (value === null || value === undefined) return value;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(v => sanitizePii(v, { shapeOnly, depth: depth + 1 }));
  }
  if (typeof value !== 'object') return '[unloggable]';

  const result: Record<string, unknown> = {};
  for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
    if (PII_KEYS.has(key)) {
      if (shapeOnly) {
        result[key] = describeShape(v);
      } else {
        result[key] = typeof v === 'string' ? `[REDACTED:len=${v.length}]` : '[REDACTED]';
      }
      continue;
    }
    result[key] = sanitizePii(v, { shapeOnly, depth: depth + 1 });
  }
  return result;
}

function describeShape(v: unknown): string {
  if (v === null) return 'null';
  if (v === undefined) return 'undefined';
  if (typeof v === 'string') return v.length === 0 ? 'empty' : `string(len=${v.length})`;
  if (Array.isArray(v)) return `array(len=${v.length})`;
  return typeof v;
}
